/**
 * Configuration des liens d'affiliation
 *
 * StockX (programme "The Connect", via Impact):
 * - Inscris-toi comme éditeur sur Impact puis postule au programme StockX.
 * - Une fois approuvé, génère un lien profond vers n'importe quelle sneaker. Il ressemble à :
 *     https://stockx.pxf.io/c/1234567/1023711/9060?u=https%3A%2F%2Fstockx.com%2Fnike-air-bakin-og-2026
 *   et se découpe ainsi : https://<host>/c/<partnerId>/<adId>/<programId>?u=<page StockX>
 * - Renseigne `impact` ci-dessous. Tant qu'il vaut `null`, le bouton pointe directement vers la fiche
 *   StockX (sans commission) et la mention "liens partenaires" n'est pas affichée.
 */

export interface ImpactConfig {
  /** Sous-domaine de tracking du lien profond, ex: "stockx.pxf.io" */
  host: string;
  /** Ton identifiant d'éditeur (1er nombre après /c/) */
  partnerId: string;
  /** Identifiant du lien / de la bannière (2e nombre) */
  adId: string;
  /** Identifiant du programme StockX (3e nombre) */
  programId: string;
}

export const affiliateConfig: {
  stockx: { baseUrl: string; impact: ImpactConfig | null };
  amazon: { trackingId: string; domain: string };
} = {
  stockx: {
    baseUrl: 'stockx.com',
    impact: null,
  },
  // Plus de bouton Amazon : les recherches ne tombent presque jamais sur la bonne paire
  // (éditions limitées absentes d'Amazon). Config conservée au cas où.
  amazon: {
    trackingId: 'sneakguessr-21',
    domain: 'amazon.fr',
  },
};

/** true quand les identifiants Impact sont renseignés, c'est-à-dire que les clics rapportent une commission. */
export function isStockXAffiliateActive(): boolean {
  return affiliateConfig.stockx.impact !== null;
}

/**
 * Build a retailer search query from a StockX product title.
 *
 * StockX titles already start with the brand ("Nike Air Foamposite...") and carry
 * release-year / audience suffixes ("(2026)", "(Women's)") that retailers don't use,
 * so searching the raw title (or brand + title) returns noise or nothing.
 */
export function buildSearchQuery(sneakerName: string, brand?: string): string {
  const name = sneakerName
    .replace(/\((?:19|20)\d\d(?:\/\d{2,4})?\)/g, '') // (2026), (2025/2026)
    .replace(/\((?:Women's|Men's|GS|PS|TD|Kids?)\)/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  // Only prepend the brand when the title doesn't already mention it
  const alreadyHasBrand = !!brand && name.toLowerCase().includes(brand.toLowerCase());
  return brand && !alreadyHasBrand ? `${brand} ${name}` : name;
}

/**
 * URL StockX pour acheter cette paire.
 * - avec `slug` : la fiche produit exacte (toujours le cas avec le catalogue KicksDB)
 * - sans `slug` : recherche StockX en dernier recours
 * Enveloppée dans le lien de suivi Impact dès que `affiliateConfig.stockx.impact` est renseigné.
 */
export function getStockXUrl(sneaker: { slug?: string; name: string; brand?: string }): string {
  const { baseUrl, impact } = affiliateConfig.stockx;

  const target = sneaker.slug
    ? `https://${baseUrl}/${sneaker.slug}`
    : `https://${baseUrl}/search?s=${encodeURIComponent(buildSearchQuery(sneaker.name, sneaker.brand))}`;

  if (!impact) return target;

  return `https://${impact.host}/c/${impact.partnerId}/${impact.adId}/${impact.programId}?u=${encodeURIComponent(target)}`;
}

/**
 * Génère une URL de recherche Amazon avec affiliation (non utilisée dans l'UI pour l'instant)
 */
export function getAmazonSearchUrl(sneakerName: string, brand?: string): string {
  const encodedQuery = encodeURIComponent(buildSearchQuery(sneakerName, brand));
  const { trackingId, domain } = affiliateConfig.amazon;

  // linkCode=ur2 est le code recommandé pour les liens de recherche texte
  return `https://www.${domain}/s?k=${encodedQuery}&tag=${trackingId}&linkCode=ur2&ref_=nb_sb_noss`;
}
